import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { dashboard } from '../api';
import { formatCurrency, formatDate, getCurrencySymbol, isOverdue } from '../utils/format';
import StatusBadge from '../components/StatusBadge';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { PERIOD_OPTIONS } from '../utils/periods';

const InfoTooltip = ({ text }) => (
  <span className="relative group inline-flex items-center ml-1">
    <span className="w-3.5 h-3.5 rounded-full bg-gray-200 text-gray-500 text-[10px] flex items-center justify-center cursor-help select-none">
      i
    </span>
    <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-52 px-2.5 py-1.5 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-20 leading-snug">
      {text}
    </span>
  </span>
);

const StatCard = ({ label, value, sub, color = 'blue', icon, tooltip }) => {
  const colors = {
    blue:   'bg-blue-50 text-blue-600',
    green:  'bg-green-50 text-green-600',
    orange: 'bg-orange-50 text-orange-600',
    red:    'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  return (
    <div className="card">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 flex items-center">
            {label}
            {tooltip && <InfoTooltip text={tooltip} />}
          </p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
        <span className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
          {icon}
        </span>
      </div>
    </div>
  );
};

const outstandingClass = (inv) => {
  const outstanding = inv.total_amount - (inv.amount_paid || 0);
  if (outstanding <= 0) return 'text-gray-400';
  if (isOverdue(inv)) return 'text-red-600 font-semibold';
  if (inv.amount_paid > 0) return 'text-amber-600 font-semibold';
  return 'text-gray-900';
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all_time');

  useEffect(() => {
    dashboard.get(period).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [period]);

  if (loading && !data) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-4 border-primary-700 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!data) return <div className="text-center text-gray-500 py-20">Failed to load dashboard</div>;

  const { stats, recent_invoices, monthly_revenue } = data;

  const chartData = monthly_revenue.map(m => ({
    month: m.month,
    Revenue: parseFloat((m.total || 0).toFixed(2)),
  }));

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-sm text-gray-500">Welcome to BillTrack</p>
          </div>
          <Link to="/invoices/new" className="btn-primary flex items-center gap-1 text-sm px-3 py-2 flex-shrink-0">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden sm:inline">New Invoice</span>
            <span className="sm:hidden">New</span>
          </Link>
        </div>
        <select className="input w-full sm:w-auto text-sm" value={period} onChange={e => setPeriod(e.target.value)}>
          {PERIOD_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Total Invoiced"
          value={formatCurrency(stats.total_invoiced)}
          color="blue"
          tooltip="Total value of all invoices raised in the selected period"
          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
        />
        <StatCard
          label="Total Received"
          value={formatCurrency(stats.total_paid)}
          color="green"
          tooltip="Total payments received in the selected period"
          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
        />
        <StatCard
          label="Total Outstanding"
          value={formatCurrency(stats.total_outstanding)}
          color="orange"
          tooltip="Total unpaid amount on invoices raised in the selected period"
          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
        />
        <StatCard
          label="Overdue"
          value={formatCurrency(stats.total_overdue)}
          sub={`${stats.overdue_count} invoice${stats.overdue_count !== 1 ? 's' : ''}`}
          color="red"
          tooltip="Total unpaid amount on invoices past their due date in the selected period"
          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Revenue Chart */}
        <div className="xl:col-span-2 card">
          <h2 className="text-base font-semibold text-gray-900 mb-4">
            Revenue <span className="text-gray-400 font-normal">({PERIOD_OPTIONS.find(o => o.value === period)?.label})</span>
          </h2>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} barSize={32}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `${getCurrencySymbol()}${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={v => formatCurrency(v)} />
                <Bar dataKey="Revenue" fill="#2E86C1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-52 flex items-center justify-center text-gray-400 text-sm">
              No payment data yet
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="card">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Quick Stats</h2>
          <div className="space-y-4">
            {[
              { label: 'Total Clients', value: stats.client_count, icon: '👥' },
            ].map(s => (
              <div key={s.label} className="flex items-center justify-between py-2 border-b border-gray-50">
                <span className="text-sm text-gray-600">{s.icon} {s.label}</span>
                <span className="font-semibold text-gray-900">{s.value}</span>
              </div>
            ))}
            {data.status_counts.map(s => (
              <div key={s.status} className="flex items-center justify-between py-2 border-b border-gray-50">
                <StatusBadge status={s.status} />
                <span className="font-semibold text-gray-900">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Invoices */}
      {/* Recent Invoices */}
<div className="card mt-6">
  <div className="flex items-center justify-between mb-4">
    <h2 className="text-base font-semibold text-gray-900">Recent Invoices</h2>
    <Link to="/invoices" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
      View all →
    </Link>
  </div>

  {recent_invoices.length === 0 ? (
    <div className="text-center py-8 text-gray-400 text-sm">
      No invoices yet.{' '}
      <Link to="/invoices/new" className="text-primary-600 hover:underline">
        Create your first invoice
      </Link>
    </div>
  ) : (
    <>
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left py-3 text-gray-500 font-medium">Invoice</th>
              <th className="text-left py-3 text-gray-500 font-medium">Client</th>
              <th className="text-left py-3 text-gray-500 font-medium">Date</th>
              <th className="text-right py-3 text-gray-500 font-medium">Amount</th>
              <th className="text-right py-3 text-gray-500 font-medium">Paid</th>
              <th className="text-right py-3 text-gray-500 font-medium">Outstanding</th>
              <th className="text-center py-3 text-gray-500 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {recent_invoices.map(inv => {
              const outstanding = inv.total_amount - (inv.amount_paid || 0);
              return (
                <tr key={inv.id} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="py-3">
                    <Link to={`/invoices/${inv.id}`} className="text-primary-600 hover:underline font-medium">
                      {inv.invoice_number}
                    </Link>
                  </td>
                  <td className="py-3 text-gray-700">{inv.client_name}</td>
                  <td className="py-3 text-gray-500">{formatDate(inv.invoice_date)}</td>
                  <td className="py-3 text-right font-semibold text-gray-900">{formatCurrency(inv.total_amount)}</td>
                  <td className="py-3 text-right text-green-600">
                    {inv.amount_paid > 0 ? formatCurrency(inv.amount_paid) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className={`py-3 text-right ${outstandingClass(inv)}`}>
                    {outstanding > 0 ? formatCurrency(outstanding) : formatCurrency(0)}
                  </td>
                  <td className="py-3 text-center">
                    <StatusBadge status={isOverdue(inv) && inv.status === 'sent' ? 'overdue' : inv.status} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {recent_invoices.map(inv => {
          const outstanding = inv.total_amount - (inv.amount_paid || 0);
          return (
            <Link
              key={inv.id}
              to={`/invoices/${inv.id}`}
              className="block border border-gray-100 rounded-lg p-4 hover:bg-gray-50"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-primary-600 font-semibold text-sm">
                  {inv.invoice_number}
                </span>
                <StatusBadge status={isOverdue(inv) && inv.status === 'sent' ? 'overdue' : inv.status} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-700 text-sm">{inv.client_name}</span>
                <span className="font-bold text-gray-900 text-sm">{formatCurrency(inv.total_amount)}</span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-gray-400 text-xs">{formatDate(inv.invoice_date)}</span>
                {outstanding > 0 && (
                  <span className={`text-xs ${outstandingClass(inv)}`}>
                    Due: {formatCurrency(outstanding)}
                  </span>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </>
  )}
</div>
    </div>
  );
}
